// btw-front/src/components/sections/Services.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Container from "../ui/Container";
import { Fade } from "react-awesome-reveal";
import { useTranslation } from "react-i18next";

// 시안 핵심 3축(앞 3개) + 보조 솔루션(나머지). 앞 3개 = SSL자동화·미들웨어·API/웹.
const SERVICE_META = [
  { key: "certbear", logo: ["logos/logo_webaresoft.png"], url: "https://www.webaresoft.com" },
  { key: "tmax", logo: ["logos/logo_tmaxsoft_blue.png"], url: "https://www.tmaxsoft.com/kr/main" },
  {
    key: "netfunnel",
    logo: ["logos/logo_stclab.png"],
    url: "https://www.stclab.com/product/api-netfunnel",
  },
  { key: "everpure", logo: ["everpure/logo_everpure.png"], url: "https://www.purestorage.com/kr" },
  { key: "elastic", logo: ["logos/logo_elastic.png"], url: "https://www.elastic.co/kr" },
  { key: "oss", logo: ["logos/logo_nginx.png", "logos/logo_apache.png"] },
  { key: "renewal", logo: ["btwsoft/logo_text_bgx.png"] },
];

const PRIMARY_COUNT = 3;

const POPOVER_WIDTH = 440;
const PAD = 12;

export default function Services() {
  const { t } = useTranslation();

  // i18n 기반 services 생성
  const services = useMemo(() => {
    return SERVICE_META.map((m) => {
      const base = `services.items.${m.key}`;
      return {
        title: t(`${base}.title`),
        logo: m.logo,
        url: m.url,
        desc: t(`${base}.desc`),
        tags: t(`${base}.tags`, { returnObjects: true }) || [],
        extra: {
          headline: t(`${base}.extra.headline`),
          bullets: t(`${base}.extra.bullets`, { returnObjects: true }) || [],
        },
      };
    });
  }, [t]);

  const [openIndex, setOpenIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [pos, setPos] = useState(null);
  const popoverRef = useRef(null);

  const activeService = useMemo(() => {
    if (openIndex == null) return null;
    return services[openIndex] ?? null;
  }, [openIndex, services]);

  const close = () => {
    setOpenIndex(null);
    setAnchorEl(null);
    setAnchorRect(null);
    setPos(null);
  };

  const computeInitialPos = (rect) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(POPOVER_WIDTH, vw - PAD * 2);

    let left = rect.left;
    left = Math.min(left, vw - width - PAD);
    left = Math.max(left, PAD);

    let top = rect.bottom + 10;
    top = Math.min(top, vh - PAD);

    return { top, left, width };
  };

  const onCardClick = (e, idx) => {
    const el = e.currentTarget;
    if (!el) return;

    const rect = el.getBoundingClientRect();

    setOpenIndex((prev) => {
      if (prev === idx) {
        close();
        return null;
      }
      setAnchorEl(el);
      setAnchorRect(rect);
      setPos(computeInitialPos(rect));
      return idx;
    });
  };

  useEffect(() => {
    const onPointerDownCapture = (e) => {
      if (openIndex == null) return;

      const popEl = popoverRef.current;
      const insidePopover = popEl?.contains(e.target);
      const insideCard = anchorEl?.contains(e.target);

      if (!insidePopover && !insideCard) close();
    };

    const onKeyDown = (e) => {
      if (openIndex == null) return;
      if (e.key === "Escape") close();
    };

    document.addEventListener("pointerdown", onPointerDownCapture, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDownCapture, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openIndex, anchorEl]);

  useEffect(() => {
    if (openIndex == null || !anchorEl) return;

    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = anchorEl.getBoundingClientRect();
        setAnchorRect(rect);
        const initial = computeInitialPos(rect);
        setPos((prev) => (prev ? { ...prev, ...initial } : initial));
      });
    };

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [openIndex, anchorEl]);

  useEffect(() => {
    if (openIndex == null || !anchorRect || !pos) return;

    const el = popoverRef.current;
    if (!el) return;

    const vh = window.innerHeight;
    const vw = window.innerWidth;

    const popH = el.offsetHeight;
    const width = pos.width;

    const spaceBelow = vh - anchorRect.bottom;
    const spaceAbove = anchorRect.top;

    let top =
      spaceBelow >= popH + 20 || spaceBelow >= spaceAbove
        ? anchorRect.bottom + 10
        : anchorRect.top - popH - 10;

    top = Math.min(top, vh - popH - PAD);
    top = Math.max(top, PAD);

    let left = pos.left;
    left = Math.min(left, vw - width - PAD);
    left = Math.max(left, PAD);

    setPos({ ...pos, top, left });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIndex, anchorRect]);

  return (
    <section id="services" className="btw-section bg-slate-100">
      <Container>
        <Fade direction="up" triggerOnce className="h-full">
          <div className="max-w-2xl">
            <span className="btw-eyebrow">Solutions</span>
            <h2 className="btw-h2 mt-4">{t("section.servicesTitle")}</h2>
            <p className="btw-lead">{t("section.servicesDesc")}</p>
          </div>
        </Fade>

        {/* 핵심 3축 */}
        <div className="mt-10 grid gap-6 grid-cols-[repeat(auto-fit,minmax(18rem,1fr))] xl:gap-7 whitespace-pre-line">
          {services.slice(0, PRIMARY_COUNT).map((s, i) => (
            <Fade key={`${s.title}-${i}`} direction="up" triggerOnce className="h-full">
              <ServiceCard
                isActive={openIndex === i}
                {...s}
                flagship={i === 0}
                onClick={(e) => onCardClick(e, i)}
              />
            </Fade>
          ))}
        </div>

        {/* 보조 솔루션 */}
        {services.length > PRIMARY_COUNT && (
          <div className="mt-14 border-t border-slate-200 pt-10">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t("services.moreTitle")}
              </span>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                {t("services.moreDesc")}
              </p>
            </div>
            <div className="mt-6 grid gap-4 grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] whitespace-pre-line">
              {services.slice(PRIMARY_COUNT).map((s, i) => {
                const idx = i + PRIMARY_COUNT;
                return (
                  <Fade key={`${s.title}-${idx}`} direction="up" triggerOnce className="h-full">
                    <ServiceCard
                      isActive={openIndex === idx}
                      {...s}
                      onClick={(e) => onCardClick(e, idx)}
                    />
                  </Fade>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-8 text-xs text-slate-500">{t("services.note")}</div>

        {openIndex != null && activeService && pos
          ? createPortal(
              <>
                <div className="fixed inset-0 z-[90]" />
                <div
                  ref={popoverRef}
                  className="fixed z-[100] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg"
                  style={{ top: pos.top, left: pos.left, width: pos.width }}
                  role="dialog"
                  aria-modal="false"
                >
                  <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {activeService.title}
                      </div>
                      {activeService.url ? (
                        <a
                          href={activeService.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-accent hover:underline"
                        >
                          {activeService.url}
                          <span aria-hidden="true">↗</span>
                        </a>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      onClick={close}
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                      aria-label={t("services.popover.close")}
                    >
                      {t("services.popover.close")}
                    </button>
                  </div>

                  <div className="px-5 py-4">
                    <div className="text-sm leading-relaxed text-slate-700">
                      <div className="font-medium text-slate-900">
                        {activeService.extra?.headline ?? t("services.popover.detailFallback")}
                      </div>

                      <p className="mt-2 text-slate-600">{activeService.desc}</p>

                      {activeService.extra?.bullets?.length ? (
                        <ul className="mt-3 list-disc space-y-1 pl-5 text-slate-600">
                          {activeService.extra.bullets.map((b) => (
                            <li key={b}>{b}</li>
                          ))}
                        </ul>
                      ) : null}

                      <div className="mt-4">
                        <div className="text-xs font-semibold text-slate-900">
                          {t("services.popover.keywords")}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {activeService.tags.map((tt) => (
                            <span
                              key={tt}
                              className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700"
                            >
                              {tt}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>,
              document.body,
            )
          : null}
      </Container>
    </section>
  );
}

function ServiceCard({ title, desc, tags, logo, onClick, isActive, flagship = false }) {
  const { t } = useTranslation();
  const logos = Array.isArray(logo) ? logo : [logo];

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(e);
        }
      }}
      className={[
        "btw-card-hover group relative flex h-full flex-col overflow-hidden bg-white p-6 cursor-pointer",
        isActive ? "border-slate-300 shadow-sm" : "",
      ].join(" ")}
      aria-expanded={isActive}
    >
      {flagship && (
        <span className="absolute right-3 top-3 inline-flex items-center rounded-full bg-accent px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm">
          {t("services.flagshipBadge")}
        </span>
      )}
      <div className="flex flex-col gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="break-keep text-base font-semibold leading-snug tracking-tight text-slate-900 sm:text-lg">
            {title}
          </h3>
        </div>

        <div className="shrink-0">
          <div className="flex h-12 w-auto max-w-[7.5rem] items-center justify-center rounded-xl border border-slate-100 bg-slate-50/60 px-3 transition-transform duration-300 group-hover:scale-105 sm:max-w-[11rem] motion-reduce:group-hover:scale-100">
            {logos.slice(0, 2).map((path) => {
              const src = `${process.env.PUBLIC_URL}/images/${path}`;
              return (
                <img
                  key={path}
                  src={src}
                  alt={`${title} logo`}
                  loading="lazy"
                  className="max-h-7 w-auto max-w-full object-contain"
                />
              );
            })}
          </div>
        </div>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-slate-600">{desc}</p>

      <div className="mt-auto flex flex-wrap gap-2 pt-5">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700"
          >
            {t}
          </span>
        ))}
      </div>

      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-slate-900/5 blur-2xl transition group-hover:bg-slate-900/10" />
    </div>
  );
}
